import {useEffect} from 'react';
import axios from 'axios';
import {useTranslation} from 'react-i18next';
import {ServerErrorMessage} from '../../definitions/Server';
import {useToast} from '../../hooks';
import {useOkta} from '../../hooks';

const AxiosApi = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const useAPI = () => {
  const {token, logout, refreshLogin} = useOkta();
  const {t} = useTranslation();
  const {showErrorToast} = useToast();

  useEffect(() => {
    // Request interceptor - Add token to all requests
    const requestInterceptor = AxiosApi.interceptors.request.use(
      config => {
        if (token && config.headers) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      },
    );

    // Response interceptor - Handle token refresh
    const responseInterceptor = AxiosApi.interceptors.response.use(
      response => {
        return response;
      },
      async error => {
        const originalRequest = error.config;
        const errorStatus = error?.response?.status;
        const errorMessage = error.response?.data
          ?.message as ServerErrorMessage;

        if (errorStatus === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          if (errorMessage === ServerErrorMessage.E_EXPIRED_TOKEN) {
            try {
              // Try to refresh the token
              await refreshLogin();

              // The token should be updated by the hook after refresh
              // We'll retry without manually setting the token since
              // the request interceptor will add the updated token
              return AxiosApi(originalRequest);
            } catch (refreshError) {
              // If refresh fails, logout user
              logout();
              showErrorToast({
                message: t('Authentication.tokenRefreshError'),
              });
              return Promise.reject(refreshError);
            }
          } else {
            // Other 401 errors, logout immediately
            logout();
            showErrorToast({
              message: t('Authentication.authError'),
            });
          }
        }

        return Promise.reject(error);
      },
    );

    // Cleanup interceptors on unmount
    return () => {
      AxiosApi.interceptors.request.eject(requestInterceptor);
      AxiosApi.interceptors.response.eject(responseInterceptor);
    };
  }, [token, logout, refreshLogin, t, showErrorToast]);

  return {
    api: AxiosApi,
  };
};

export const _api = AxiosApi;
